import { AcumaticaRequestOptions, AcumaticaService } from '../acumatica.service';
import { AcumaticaEntity, AcumaticaEntityService, GenericEntityService } from './acumatica-entity';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Acumatica, AcumaticaFieldFormat, AcumaticaStringList } from '../acumatica-field-decorator';
import { Address, Contact, DocContact } from './Contacts.service';

@Injectable({ providedIn: 'root' })
export class CustomerSerivce extends AcumaticaEntityService {
    constructor(
        acumatica: AcumaticaService,
        private entities: GenericEntityService,
    ) {
        super(acumatica, 'Customer', Customer);
    }

    get(CustomerID: string, options: AcumaticaRequestOptions = {}): Observable<Customer> {
        return super.getEntity(CustomerID, {
            ...{ loader: "Getting Customer" },
            ...options
        })
    }

    put(customer: Customer, options: AcumaticaRequestOptions = {}): Observable<Customer> {
        return new Observable<Customer>(subscriber => {
            super.putEntity(customer, options).subscribe(
                customer => {
                    subscriber.next(customer);
                    subscriber.complete();
                },
                error => {
                    subscriber.error(error);
                    subscriber.complete();
                });
        });
    }

    search(searchVal: string, options: AcumaticaRequestOptions = {}): Observable<AcumaticaEntity[]> {
        return new Observable<AcumaticaEntity[]>(subscriber => {
            let search = new AcumaticaEntity({
                search: searchVal
            });

            options = {
                ...{
                    expand: 'CustomerSearchDetails',
                    loader: 'Searching Customers...'
                },
                ...options
            };

            this.entities.inquiry('CustomerSearch', search, options).subscribe(result => {
                subscriber.next(result.CustomerSearchDetails);
                subscriber.complete();
            });
        });
    }
}

@Injectable({ providedIn: 'root' })
export class CustomerLocationService extends AcumaticaEntityService {
    constructor(
        acumatica: AcumaticaService,
    ) {
        super(acumatica, 'CustomerLocation', CustomerLocation);
    }

    get(CustomerID: string, LocationID: string, options: AcumaticaRequestOptions = {}): Observable<CustomerLocation> {
        return super.getEntity([CustomerID, LocationID], {
            ...{ loader: "Getting Customer Location" },
            ...options
        })
    }
}

export interface CustomerAddress {
    AddressLine1?: string | null;
    AddressLine2?: string | null;
    City?: string | null;
    State?: string | null;
    PostalCode?: string | null;
}

export class CustomerLocation extends AcumaticaEntity {
    @Acumatica() LocationID!: string;
    @Acumatica() LocationName: string | null = null;
    @Acumatica() Active: boolean = false;
    @Acumatica() Default: boolean = false;

    @Acumatica() AddressLine1: string | null = null;
    @Acumatica() AddressLine2: string | null = null;
    @Acumatica() City: string | null = null;
    @Acumatica() Country: string | null = null;
    @Acumatica() PostalCode: string | null = null;
    @Acumatica() State: string | null = null;
    @Acumatica() StateName: string | null = null;

    @Acumatica() Attention: string | null = null;
    @Acumatica() Email: string | null = null;
    @Acumatica() AccountName: string | null = null;
    @Acumatica() Phone1: string | null = null;

    public GetOrderAddress(): Address {
        return new Address({
            AddressLine1: this.AddressLine1,
            AddressLine2: this.AddressLine2,
            City: this.City,
            State: this.State,
            PostalCode: this.PostalCode,
            Country: this.Country,
        });
    }

    public GetOrderContact(): DocContact {
        return new DocContact({
            Attention: this.Attention,
            Email: this.Email,
            BusinessName: this.AccountName,
            Phone1: this.Phone1
        });
    }
}

export const CustomerSalesStatusList = new AcumaticaStringList([
    ['', '(unspecified)'],
    // ['NL', 'New Lead / No Account'],
    ['NR', 'Reached Out - No Response'],
    ['AS', 'Recent Appointment / Appointment set'],
    ['NS', 'Receive eBlasts - No Sales Appts'],
])

export class CustomerSampleBook extends AcumaticaEntity {
    @Acumatica() InventoryID!: string;
    @Acumatica({ write: false }) Description?: string;
    @Acumatica({ format: AcumaticaFieldFormat.Date }) LastChecked: Date | null = null;
    @Acumatica() LastModifiedBy?: string;
    @Acumatica('SampleBookNotes') Notes: string | null = null;
}

export class Customer extends AcumaticaEntity {
    @Acumatica() CustomerID?: string;
    @Acumatica() CustomerName?: string;
    @Acumatica(CustomerLocation) Locations?: CustomerLocation[];
    @Acumatica(CustomerSampleBook) SampleBooks?: CustomerSampleBook[];
    @Acumatica() CustomerClass?: string;
    @Acumatica() CustomerClassDescription?: string;
    @Acumatica() Status?: string;
    @Acumatica() CustomLabelReportID?: string | null;
    @Acumatica() AcceptsPFAS!: boolean | null;
    @Acumatica({write:false}) PriceListAuthToken?: string | null;
    @Acumatica({name: 'PriceClassID', write:false}) PriceLevel?: string | null;
    @Acumatica({name: 'PriceClassIDDesc', write:false}) PriceLevelDesc?: string | null;

    // General > Account Address
    @Acumatica() AddressLine1?: string;
    @Acumatica() AddressLine2?: string;
    @Acumatica() City?: string;
    @Acumatica() State?: string;
    @Acumatica() PostalCode?: string;
    @Acumatica() Country?: string;

    // Genearl > Additional Account Info
    @Acumatica() Phone1Type?: string;
    @Acumatica() Phone1?: string;
    @Acumatica() Phone2Type?: string;
    @Acumatica() Phone2?: string;
    @Acumatica() FaxType?: string;
    @Acumatica() Fax?: string;
    @Acumatica() Email?: string;
    @Acumatica() Web?: string;

    @Acumatica({ format: AcumaticaFieldFormat.Date, write: false }) DateOpened!: Date;
    @Acumatica({ write: false }) Age!: string;
    @Acumatica() RepNotes?: string;
    @Acumatica(CustomerSalesStatusList) SalesStatus?: string;
    @Acumatica() NextAppt?: Date;

    @Acumatica(Contact) PrimaryContact?: Contact;

    get CustomerIDName() {
        return this.CustomerID + " - " + this.CustomerName;
    }
}
